<template>
  <div class="m-table">
    <table>
      <thead>
        <template v-for="(header, index) in thead">
          <th
            colspan="2"
            :style="`width: ${header.width}%`"
            :key="index"
            v-if="actions && (thead.length - 1) === index">
              {{ header.value }}
            </th>
          <th
            :style="`width: ${header.width}%`"
            :key="index"
            v-else>{{ header.value }}
          </th>
        </template>
      </thead>
      <tbody v-if="loading">
        <td :colspan="colspan">
          <VLoadingSpinner :overlay="false" size="medium" />
        </td>
      </tbody>
      <tbody v-if="tbody.length === 0 && loading === false">
        <tr>
          <td :colspan="colspan" style="text-align: center;">
            <VMessage type="warning" subtype="basic">
              <template v-slot:icon>
                <InfoIcon />
              </template>
              <template v-slot:text>
                {{ noResultsMessage }}
              </template>
            </VMessage>
          </td>
        </tr>
      </tbody>
      <tbody v-else>
        <tr
          v-for="(tableData, trIndex) in modifiedTbody"
          :key="tbody[trIndex].id"
          :class="rowClass(tbody[trIndex])"
        >
          <td
            v-for="(tableKey, tdIndex) in Object.keys(tableData)"
            :key="tdIndex"
            :class="`m-table__td m-table__td--${tableKeyClass(tableKey)}`"
            v-if="tableKey !== 'actions'">
            <template v-if="tableData[tableKey] instanceof Array">
              <VLabel
                v-for="(label, labelIndex) in tableData[tableKey]"
                type="info"
                v-if="label.value"
                :key="labelIndex">
                <template v-slot:text>
                  {{ $t(label.i18n) }}
                </template>
              </VLabel>
            </template>
            <span v-else-if="anchorify[tableKey]" :data-table-column="tableKey">
              <a @click="anchorify[tableKey]" :data-id="trIndex" v-if="nativeLink">
                {{ tableData[tableKey] }}
              </a>
              <RouterLink
                :to="anchorify[tableKey](tbody[trIndex])"
                v-else-if="stringFormatter[tableKey]">
                {{ stringFormatter[tableKey](tbody[trIndex]) }}
              </RouterLink>
            </span>
            <span v-else-if="dateFormatter[tableKey]">{{ formatDate(tableData[tableKey]) }}</span>
            <span
              v-else-if="stringFormatter[tableKey]"
              v-html="stringFormatter[tableKey](tbody[trIndex])">
            </span>
            <span v-else>
              {{ traverse(tbody[trIndex], tableKey) }}
            </span>
          </td>

          <td class="m-table__td m-table__td--actions" v-if="actions">
            <slot v-bind="tbody[trIndex]" />
          </td>
        </tr>

        <tr v-if="collapse && tbody.length > collapseAfter" @click="toggleResults">
          <td
            :colspan="colspan"
            class="m-table__td m-table__td--more-link"
            :class="{ 'm-table__td--no-shadow': !isCollapsed }"
          >
            <a class="g-link">
              <PlusCircleIcon v-if="isCollapsed" />
              <MinusCircleIcon v-else />
              {{ isCollapsed
                ? `show ${tbody.length - 5} more result(s)`
                : `hide ${tbody.length - 5} less result(s)`
              }}
            </a>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import Moment from 'moment';
import VLabel from '@/components/deprecated/atoms/Label.vue';
import VLoadingSpinner from '@/components/deprecated/atoms/LoadingSpinner.vue';
import VMessage from '@/components/deprecated/atoms/Message.vue';
import { InfoIcon, MinusCircleIcon, PlusCircleIcon } from '@/components/icons/fontIcons';

export default {
  name: 'VTable',
  introduction: 'To be deprecated',
  props: {
    thead: {
      type: [Array, Object],
      required: true,
    },
    tbody: {
      type: [Array],
      required: true,
    },
    loading: {
      type: [Boolean],
      default: false,
    },
    noResultsMessage: {
      type: [String],
    },
    collapse: {
      type: [Boolean],
      default: false,
    },
    collapseAfter: {
      type: [Number],
      default: 5,
    },
    nativeLink: {
      type: [Boolean],
      default: true,
    },
    columns: {
      type: [Array],
      required: true,
      default: () => [],
    },
    anchorify: {
      required: false,
      type: [Object],
      default: () => ({}),
    },
    dateFormatter: {
      required: false,
      type: [Object],
      default: () => ({}),
    },
    stringFormatter: {
      required: false,
      type: [Object],
      default: () => ({}),
    },
    rowClass: {
      required: false,
      type: [Function],
      default: () => ({}),
    },
    actions: {
      required: false,
      type: [Boolean],
      default: false,
    },
  },
  components: {
    VLabel,
    VLoadingSpinner,
    VMessage,
    InfoIcon,
    PlusCircleIcon,
    MinusCircleIcon,
  },
  data() {
    return {
      modifiedTbody: [],
      isCollapsed: false,
    };
  },
  created() {
    this.isCollapsed = this.collapse;
    this.organizeColumns();
  },
  computed: {
    colspan() {
      return this.thead.length + (this.actions ? 1 : 0);
    },
  },
  methods: {
    formatDate(UnixDate) {
      if (UnixDate <= 0) {
        return '-';
      }

      return Moment.unix(UnixDate).format('ll');
    },
    traverse(object, keys) {
      return keys.split('.').reduce((cur, key) => cur[key], object);
    },
    organizeColumns() {
      if (this.columns.length === 0) {
        return [];
      }
      const table = [];
      this.tbody.forEach((item, index) => {
        const items = {};
        for (let i = 0; this.columns.length > i; i += 1) {
          const key = this.columns[i];
          items[key] = item[key];
        }
        if (this.collapse && this.isCollapsed) {
          if (this.collapseAfter > index) {
            table.push(items);
          }
        } else {
          table.push(items);
        }
        return items;
      });
      this.modifiedTbody = table;
      return table;
    },
    tableKeyClass(tableKey) {
      return tableKey.replace(/\./i, '-');
    },
    toggleResults() {
      this.isCollapsed = !this.isCollapsed;
    },
  },
  watch: {
    tbody() {
      this.organizeColumns();
    },
    isCollapsed() {
      this.organizeColumns();
    },
  },
};
</script>

<style scoped>
.m-table {
  width: 100%;
  max-width: 100%;
  overflow-x: scroll;
}

.m-table__td--more-link {
  text-align: center;
  font-weight: bold;
  cursor: pointer;
  box-shadow: 0px -5px 13px -5px var(--signable-secondary-4-shade-2);
  padding: var(--spacing-small);
  border-top: 3px solid var(--signable-secondary-4-shade-1);
}

.m-table__td--more-link:hover {
  background: var(--signable-secondary-4-shade-3);
}

.m-table__td--more-link a:hover {
  text-decoration: none;
}

.m-table__td--no-shadow {
  box-shadow: none;
}

.m-table__td--actions {
  width: 15%;
}

@media screen and (max-width: 1245px) {
  .m-table__td--actions {
    width: 1%;
  }
  .m-table__td--actions a {
    margin-bottom: var(--spacing-xs);
  }
}

.m-table ::v-deep .a-button {
  float: right;
  margin-left: var(--spacing-xs);
}
</style>
